import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch,
  FaFilter,
  FaEdit,
  FaTrash,
  FaFileContract,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaBuilding,
  FaEye,
  FaDownload,
  FaUserCheck,
  FaFilePdf,
  FaBan,
  FaPercentage,
  FaChevronLeft,
  FaEnvelope,
  FaUpload,
  FaThumbsUp,
  FaThumbsDown,
  FaSync,
  FaArrowLeft,
  FaFileSignature,
  FaCheckDouble,
  FaLink,
  FaRedo,
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditContractModal from './EditContractModal';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;
const AQ_BASE_URL = process.env.REACT_APP_AQ_BASE_URL;

const ContractApprovalsPage = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [regeneratingLink, setRegeneratingLink] = useState(false);
  const [renewalData, setRenewalData] = useState({
    newStartDate: '',
    newEndDate: '',
    renewalNotes: '',
  });

  const itemsPerPage = 8;

  useEffect(() => {
    fetchContracts();
  }, []);

  // Format date utility function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Check if link is expired
  const isLinkExpired = (contract) => {
    if (!contract.linkExpiresAt) return true;
    return new Date() > new Date(contract.linkExpiresAt);
  };

  // Get time remaining for link
  const getTimeRemaining = (contract) => {
    if (!contract.linkExpiresAt) return 'No link generated';

    const now = new Date();
    const expires = new Date(contract.linkExpiresAt);
    const diffMs = expires - now;

    if (diffMs <= 0) return 'Expired';

    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;

    if (diffHours > 0) {
      return `${diffHours}h ${remainingMins}m`;
    }
    return `${diffMins}m`;
  };

  // Pagination variables
  const totalPages = Math.ceil(
    contracts.filter((contract) => {
      const matchesSearch =
        contract.contractname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.companyid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        contract.company?.companyname?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'pending' &&
          ['draft', 'pending', 'ready_for_approval'].includes(contract.status)) ||
        contract.status === statusFilter;

      return matchesSearch && matchesStatus;
    }).length / itemsPerPage
  );

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contracts/`, {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.contracts) {
        let transformedContracts = result.contracts.map((contract) => {
          const now = new Date();
          const endDate = new Date(contract.contract_end);
          const isExpired = endDate < now;

          // Determine workflow step based on actual contract status fields
          const getWorkflowStep = (contract) => {
            if (contract.is_approved) return 'approved';
            if (contract.is_rejected) return 'rejected';
            if (contract.pdf_uploaded && contract.pdf_signed) return 'ready_for_approval';
            if (contract.sent_pdf_on_email && !contract.pdf_signed) return 'awaiting_signature';
            if (!contract.sent_pdf_on_email) return 'sent_for_signature';
            return 'draft';
          };

          // Map backend status to frontend display status
          const getDisplayStatus = (contract) => {
            if (contract.is_approved) return 'approved';
            if (contract.is_rejected) return 'rejected';
            if (contract.pdf_uploaded && contract.pdf_signed) return 'ready_for_approval';
            if (contract.sent_pdf_on_email && !contract.pdf_signed) return 'pending';
            return 'draft';
          };

          const workflowStep = getWorkflowStep(contract);
          const displayStatus = getDisplayStatus(contract);

          return {
            contractid: contract.contractid,
            contractname: contract.contract_name,
            companyid: contract.companyid,
            contractStartDate: contract.contract_start,
            contractEndDate: contract.contract_end,
            status: displayStatus,
            workflowStep: workflowStep,
            gst_number: contract.gst_number,
            createdDate: contract.created_date,
            updatedDate: contract.updated_date,
            createdBy: 'Super_Admin',
            approvalStatus: displayStatus,
            emailSent: contract.sent_pdf_on_email,
            pdfUploaded: contract.pdf_uploaded,
            pdfSigned: contract.pdf_signed,
            isExpired: isExpired,
            accessLink: contract.download_url,
            linkExpiresAt: null,
            linkGeneratedAt: null,
            contactDetails: {
              contactPerson: contract.contact_person,
              contactEmail: contract.contact_email,
              contactPhone: contract.contact_phone,
            },
            paymentTerms: {
              billingCycle: contract.billing_cycle || 'monthly',
              paymentTerms: contract.contract_discount_structure?.[0]?.payment_terms || 'net30',
              latePaymentFee:
                parseFloat(contract.contract_discount_structure?.[0]?.late_payment_fee) || 0,
              creditlimit: parseFloat(contract.contract_discount_structure?.[0]?.credit_limit) || 0,
              baseprice: parseFloat(contract.contract_discount_structure?.[0]?.base_price) || 0,
              currency: contract.contract_discount_structure?.[0]?.currency || 'INR',
              invoicingDate: contract.contract_discount_structure?.[0]?.invoicing_date || '1',
            },
            discountStructure: {
              discountType: contract.contract_discount_structure?.[0]?.discount_type || 'flat',
              flatDiscount:
                parseFloat(contract.contract_discount_structure?.[0]?.flat_discount) || 0,
              inclusiveDiscount:
                contract.contract_discount_structure?.[0]?.inclusive_discount || true,
              discountSlabs:
                contract.contract_discount_slab?.map((slab) => ({
                  minAmount: parseFloat(slab.min_amount) || 0,
                  discountPercent: parseFloat(slab.discount_percent) || 0,
                })) || [],
            },
            withdrawalTerms: {
              withdrawalNotice: contract.contract_duration?.[0]?.withdrawal_notice || '30',
              withdrawalPenalty: contract.contract_duration?.[0]?.withdrawal_penalty || '',
              forceMajeure: contract.contract_duration?.[0]?.force_majeure || '',
              penaltyApplicable: !!contract.contract_duration?.[0]?.withdrawal_penalty,
            },
            signatoryDetails: {
              signatoryName: contract.contract_signatory?.[0]?.signatory_name || '',
              signatoryTitle: contract.contract_signatory?.[0]?.signatory_title || '',
              signatoryEmail: contract.contract_signatory?.[0]?.signatory_email || '',
              signatoryPhone: contract.contract_signatory?.[0]?.signatory_phone || '',
            },
            termsAndConditions: contract.contract_terms?.[0]?.contract_terms || '',
            autoRenewal: contract.contract_duration?.[0]?.auto_renewal || false,
            renewalNoticePeriod: contract.contract_duration?.[0]?.renewal_notice_period || 30,
            company: contract.company,
            // Backend status fields
            approved_at: contract.approved_at,
            rejected_at: contract.rejected_at,
            rejection_reason: contract.rejection_reason,
            is_approved: contract.is_approved,
            is_rejected: contract.is_rejected,
            download_url: contract.download_url,
            // Raw backend fields for accurate status determination
            sent_pdf_on_email: contract.sent_pdf_on_email,
            pdf_signed: contract.pdf_signed,
            pdf_uploaded: contract.pdf_uploaded,
            ready_for_approval: contract.ready_for_approval,
          };
        });

        setContracts(transformedContracts);
      } else {
        setContracts([]);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  // Add this function to your ContractApprovalsPage component
  const handleUpdateContract = async (contractId, updateData) => {
    try {
      setLoading(true);

      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contractId}`,
        {
          method: 'PUT',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        toast.success('Contract updated successfully!');

        // Auto-refresh contracts after update
        await fetchContracts();
      } else {
        throw new Error(result.message || 'Failed to update contract');
      }
    } catch (error) {
      console.error('Error updating contract:', error);
      toast.error(`Failed to update contract: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // API: Approve Contract
  const handleApproveContract = async (contractId) => {
    try {
      // First fetch current user to get email
      const userResponse = await fetch(`${AQ_BASE_URL}/aq/api/currentuser`, {
        credentials: 'include',
      });

      if (!userResponse.ok) {
        throw new Error('Failed to fetch user data');
      }

      const userData = await userResponse.json();
      const userEmail = userData?.email;

      if (!userEmail) {
        throw new Error('User email not found');
      }

      // Now approve contract with user email
      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/approve`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contract_id: contractId,
            email: userEmail,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Update the contract in state
        setContracts((prev) =>
          prev.map((contract) =>
            contract.contractid === contractId
              ? {
                  ...contract,
                  status: 'approved',
                  approvalStatus: 'approved',
                  workflowStep: 'approved',
                  is_approved: true,
                  is_rejected: false,
                  approved_at: result.approved_at,
                  approved_by: result.approved_by,
                  approved_by_email: result.approved_by_email,
                }
              : contract
          )
        );

        setShowApproveModal(false);
        toast.success('Contract approved successfully and pricing template generated!');

        // Refresh contracts to get latest data
        fetchContracts();
      } else {
        throw new Error(result.message || 'Failed to approve contract');
      }
    } catch (error) {
      console.error('Error approving contract:', error);
      toast.error(`Failed to approve contract: ${error.message}`);
    }
  };

  // API: Reject Contract
  const handleRejectContract = async (contractId) => {
    try {
      if (!rejectionReason.trim()) {
        toast.warning('Please provide a rejection reason');
        return;
      }

      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contractId}/reject`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            rejection_reason: rejectionReason.trim(),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Update the contract in state
        setContracts((prev) =>
          prev.map((contract) =>
            contract.contractid === contractId
              ? {
                  ...contract,
                  status: 'rejected',
                  approvalStatus: 'rejected',
                  workflowStep: 'rejected',
                  is_approved: false,
                  is_rejected: true,
                  rejected_at: result.rejected_at,
                  rejection_reason: result.rejection_reason,
                }
              : contract
          )
        );

        setShowRejectModal(false);
        setRejectionReason('');
        toast.success('Contract rejected successfully!');

        // Refresh contracts to get latest data
        fetchContracts();
      } else {
        throw new Error(result.message || 'Failed to reject contract');
      }
    } catch (error) {
      console.error('Error rejecting contract:', error);
      toast.error(`Failed to reject contract: ${error.message}`);
    }
  };

  // Download Contract using the download_url from API
  const handleDownloadContract = (contract) => {
    if (contract.download_url) {
      window.open(contract.download_url, '_blank');
      toast.info('Downloading contract document...');
    } else {
      toast.warning('No download URL available for this contract.');
    }
  };

  // Get Status Icon based on contract status
  const getStatusIcon = (contract) => {
    const status = contract.status || contract.approvalStatus;

    switch (status) {
      case 'approved':
        return <FaCheckCircle className="text-green-500 text-lg" title="Approved" />;
      case 'rejected':
        return <FaTimesCircle className="text-red-500 text-lg" title="Rejected" />;
      case 'draft':
        return <FaFileContract className="text-blue-500 text-lg" title="Draft" />;
      case 'pending':
        return <FaClock className="text-orange-500 text-lg" title="Pending Signature" />;
      case 'ready_for_approval':
        return <FaFileSignature className="text-purple-500 text-lg" title="Ready for Approval" />;
      default:
        // Fallback logic based on actual contract state
        if (contract.is_approved)
          return <FaCheckCircle className="text-green-500 text-lg" title="Approved" />;
        if (contract.is_rejected)
          return <FaTimesCircle className="text-red-500 text-lg" title="Rejected" />;
        if (contract.pdf_uploaded && contract.pdf_signed)
          return <FaFileSignature className="text-purple-500 text-lg" title="Ready for Approval" />;
        if (contract.sent_pdf_on_email && !contract.pdf_signed)
          return <FaClock className="text-orange-500 text-lg" title="Pending Signature" />;
        return <FaFileContract className="text-blue-500 text-lg" title="Draft" />;
    }
  };
  // Get Status Badge with enhanced styling
  const getStatusBadge = (contract) => {
    const status = contract.status || contract.approvalStatus;

    const statusConfig = {
      approved: {
        color: 'bg-green-100 text-green-800 border border-green-200',
        label: 'Approved',
        icon: FaCheckCircle,
      },
      rejected: {
        color: 'bg-red-100 text-red-800 border border-red-200',
        label: 'Rejected',
        icon: FaTimesCircle,
      },
      draft: {
        color: 'bg-gray-100 text-gray-700 border border-gray-300',
        label: 'Draft',
        icon: FaFileContract,
      },
      pending: {
        color: 'bg-orange-100 text-orange-800 border border-orange-200',
        label: 'Pending Signature',
        icon: FaClock,
      },
      ready_for_approval: {
        color: 'bg-purple-100 text-purple-800 border border-purple-200',
        label: 'Ready for Approval',
        icon: FaFileSignature,
      },
    };

    const config = statusConfig[status] || {
      color: 'bg-gray-100 text-gray-800',
      label: status,
      icon: FaClock,
    };
    const IconComponent = config.icon;

    return (
      <span
        className={`px-3 py-1.5 rounded-full text-xs font-medium ${config.color} flex items-center space-x-2`}
      >
        <IconComponent className="text-xs" />
        <span>{config.label}</span>
      </span>
    );
  };

  // Enhanced workflow badge that considers both status and workflow step
  const getWorkflowBadge = (contract) => {
    if (contract.isExpired) {
      return (
        <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 flex items-center space-x-2">
          <FaTimesCircle className="text-xs" />
          <span>Expired</span>
        </span>
      );
    }

    // Use status from backend as primary indicator
    return getStatusBadge(contract);
  };
  // Send for signature handler
  const handleSendForSignature = async (contract) => {
    try {
      setGeneratingContract(true);
      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contract.contractid}/generate-and-send`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Auto-refresh contracts after email is sent
        await fetchContracts();

        toast.success(`Contract sent to ${result.email_sent_to}! Access link created.`);
      } else {
        throw new Error(result.message || 'Failed to generate and send contract');
      }
    } catch (error) {
      console.error('Error generating and sending contract:', error);
      toast.error(`Failed to send contract: ${error.message}`);
    } finally {
      setGeneratingContract(false);
    }
  };

  // Upload signed contract handler
  const handleUploadSignedContract = async (contractId) => {
    if (!uploadedFile) {
      toast.warning('Please select a PDF file to upload');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('signed_pdf', uploadedFile);

      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contractId}/upload-signed`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Auto-refresh contracts after upload
        await fetchContracts();

        setShowUploadModal(false);
        setUploadedFile(null);
        toast.success('Signed contract uploaded successfully! Ready for approval.');
      } else {
        throw new Error(result.message || 'Failed to upload signed contract');
      }
    } catch (error) {
      console.error('Error uploading signed contract:', error);
      toast.error(`Upload failed: ${error.message}`);
    }
  };

  // Regenerate access link handler
  const handleRegenerateAccessLink = async (contractId, expiryMinutes = null) => {
    try {
      setRegeneratingLink(true);

      const url = expiryMinutes
        ? `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contractId}/regenerate-access-link?expiry_minutes=${expiryMinutes}`
        : `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contractId}/regenerate-access-link`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        // Auto-refresh contracts after regenerating link
        await fetchContracts();

        setShowRegenerateModal(false);
        toast.success('Access link regenerated successfully!');
      } else {
        throw new Error(result.message || 'Failed to regenerate access link');
      }
    } catch (error) {
      console.error('Error regenerating access link:', error);
      toast.error(`Regeneration failed: ${error.message}`);
    } finally {
      setRegeneratingLink(false);
    }
  };
  // File upload handler
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setUploadedFile(file);
      toast.info('PDF file selected successfully');
    } else {
      toast.error('Please upload a valid PDF file');
    }
  };

  // View contract handler
  // View contract handler - NOW FETCHES FROM API
  const handleViewContract = async (contract) => {
    try {
      setLoading(true);

      // Fetch the specific contract details from the API
      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contract.contractid}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.contract) {
        // Transform the API response to match your component's expected format
        const contractData = result.contract;

        // Transform the data similar to how you do in fetchContracts
        const transformedContract = {
          contractid: contractData.contractid,
          contractname: contractData.contract_name,
          companyid: contractData.companyid,
          contractStartDate: contractData.contract_start,
          contractEndDate: contractData.contract_end,
          status: contractData.status,
          gst_number: contractData.gst_number,
          createdDate: contractData.created_date,
          updatedDate: contractData.updated_date,
          isExpired: new Date(contractData.contract_end) < new Date(),
          download_url: contractData.download_url,
          accessLink: contractData.download_url,
          sent_pdf_on_email: contractData.sent_pdf_on_email,
          pdf_uploaded: contractData.pdf_uploaded,
          pdf_signed: contractData.pdf_signed,
          is_approved: contractData.is_approved,
          is_rejected: contractData.is_rejected,
          autoRenewal: contractData.contract_duration?.[0]?.auto_renewal || false,
          renewalNoticePeriod: contractData.contract_duration?.[0]?.renewal_notice_period || 30,

          // Contact Details
          contactDetails: {
            contactPerson: contractData.contact_person,
            contactEmail: contractData.contact_email,
            contactPhone: contractData.contact_phone,
          },

          // Payment Terms - THIS IS WHERE YOUR FINANCIAL DATA COMES FROM
          paymentTerms: {
            billingCycle: contractData.billing_cycle || 'monthly',
            paymentTerms: contractData.contract_discount_structure?.[0]?.payment_terms || 'net30',
            latePaymentFee:
              parseFloat(contractData.contract_discount_structure?.[0]?.late_payment_fee) || 0,
            creditlimit:
              parseFloat(contractData.contract_discount_structure?.[0]?.credit_limit) || 0,
            baseprice: parseFloat(contractData.contract_discount_structure?.[0]?.base_price) || 0,
            currency: contractData.contract_discount_structure?.[0]?.currency || 'INR',
            invoicingDate: contractData.contract_discount_structure?.[0]?.invoicing_date || '1',
          },

          // Discount Structure
          discountStructure: {
            discountType: contractData.contract_discount_structure?.[0]?.discount_type || 'flat',
            flatDiscount:
              parseFloat(contractData.contract_discount_structure?.[0]?.flat_discount) || 0,
            inclusiveDiscount:
              contractData.contract_discount_structure?.[0]?.inclusive_discount || true,
            discountSlabs:
              contractData.contract_discount_slab?.map((slab) => ({
                minAmount: parseFloat(slab.min_amount) || 0,
                discountPercent: parseFloat(slab.discount_percent) || 0,
              })) || [],
          },

          // Signatory Details
          signatoryDetails: {
            signatoryName: contractData.contract_signatory?.[0]?.signatory_name || '',
            signatoryTitle: contractData.contract_signatory?.[0]?.signatory_title || '',
            signatoryEmail: contractData.contract_signatory?.[0]?.signatory_email || '',
            signatoryPhone: contractData.contract_signatory?.[0]?.signatory_phone || '',
          },

          // Company Details
          company: contractData.company || {
            companyname: contractData.companyid,
          },
        };

        setSelectedContract(transformedContract);
        setShowViewModal(true);
      } else {
        toast.error('Failed to load contract details');
      }
    } catch (error) {
      console.error('Error viewing contract:', error);
      toast.error('Failed to load contract details: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Renew contract handler
  const handleRenewContract = async (contractId) => {
    try {
      if (!renewalData.newStartDate || !renewalData.newEndDate) {
        toast.warning('Please provide renewal dates');
        return;
      }

      setContracts((prev) =>
        prev.map((contract) =>
          contract.contractid === contractId
            ? {
                ...contract,
                contractStartDate: renewalData.newStartDate,
                contractEndDate: renewalData.newEndDate,
                isExpired: false,
                approvalStatus: 'pending',
                workflowStep: 'sent_for_signature',
                emailSent: false,
                pdfUploaded: false,
                accessLink: null,
                linkExpiresAt: null,
              }
            : contract
        )
      );

      setShowRenewModal(false);
      setRenewalData({ newStartDate: '', newEndDate: '', renewalNotes: '' });
      toast.success('Contract renewed successfully! Sent for new signature process.');
    } catch (error) {
      console.error('Error renewing contract:', error);
      toast.error('Failed to renew contract');
    }
  };

  // Delete contract handler
  const handleDeleteContract = async (contractId) => {
    try {
      const response = await fetch(
        `${BQ_BASE_URL || 'http://localhost:9000'}/bq/api/contract/${contractId}`,
        {
          method: 'DELETE',
          headers: {
            accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setContracts((prev) => prev.filter((contract) => contract.contractid !== contractId));
        setShowDeleteModal(false);
        toast.success('Contract deleted successfully!');
      } else {
        throw new Error(result.message || 'Failed to delete contract');
      }
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error(`Delete failed: ${error.message}`);
    }
  };

  // Enhanced action buttons based on contract status
  const getActionButtons = (contract) => {
    const status = contract.status || contract.approvalStatus;

    // If contract is already approved or rejected, show limited actions
    if (status === 'approved' || status === 'rejected') {
      return (
        <>
          {contract.download_url && (
            <button
              onClick={() => handleDownloadContract(contract)}
              className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition duration-150"
              title="Download Contract"
            >
              <FaDownload size={16} />
            </button>
          )}
        </>
      );
    }

    if (contract.isExpired) {
      return (
        <button
          onClick={() => {
            setSelectedContract(contract);
            setShowRenewModal(true);
          }}
          className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition duration-150"
          title="Renew Contract"
        >
          <FaSync size={16} />
        </button>
      );
    }

    // Show email icon only if PDF hasn't been sent yet
    if (!contract.sent_pdf_on_email) {
      return (
        <button
          onClick={() => handleSendForSignature(contract)}
          disabled={generatingContract}
          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition duration-150 disabled:opacity-50"
          title="Generate & Send Contract"
        >
          <FaEnvelope size={16} />
        </button>
      );
    }

    // Show upload icon only if PDF is sent but not uploaded/signed
    if (contract.sent_pdf_on_email && !contract.pdf_uploaded && !contract.pdf_signed) {
      return (
        <>
          <button
            onClick={() => {
              setSelectedContract(contract);
              setShowUploadModal(true);
            }}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition duration-150"
            title="Upload Signed Contract"
          >
            <FaUpload size={16} />
          </button>
          {contract.accessLink && (
            <button
              onClick={() => {
                setSelectedContract(contract);
                setShowRegenerateModal(true);
              }}
              className="p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition duration-150"
              title="Regenerate Access Link"
            >
              <FaRedo size={16} />
            </button>
          )}
        </>
      );
    }

    // Show approve/reject icons only when PDF is uploaded and signed
    if (
      contract.pdf_uploaded &&
      contract.pdf_signed &&
      !contract.is_approved &&
      !contract.is_rejected
    ) {
      return (
        <>
          <button
            onClick={() => {
              setSelectedContract(contract);
              setShowApproveModal(true);
            }}
            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition duration-150"
            title="Approve Contract"
          >
            <FaThumbsUp size={16} />
          </button>
          <button
            onClick={() => {
              setSelectedContract(contract);
              setShowRejectModal(true);
            }}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition duration-150"
            title="Reject Contract"
          >
            <FaThumbsDown size={16} />
          </button>
        </>
      );
    }

    return null;
  };
  // Filter contracts based on status and search
  const filteredContracts = contracts.filter((contract) => {
    const matchesSearch =
      contract.contractname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.companyid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.company?.companyname?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'pending' &&
        ['draft', 'pending', 'ready_for_approval'].includes(contract.status)) ||
      contract.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Enhanced Step Indicator Component
  const WorkflowSteps = () => {
    const steps = [
      {
        key: 'sent_for_signature',
        label: 'Generate & Send',
        color: 'bg-gray-400',
      },
      {
        key: 'awaiting_signature',
        label: 'Awaiting Signature',
        color: 'bg-gray-500',
      },
      {
        key: 'ready_for_approval',
        label: 'Ready for Approval',
        color: 'bg-gray-700',
      },
      { key: 'approved', label: 'Contract Active', color: 'bg-black' },
    ];

    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval Workflow</h3>
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center flex-1">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${step.color} text-white font-semibold text-sm mb-2`}
                >
                  {index + 1}
                </div>
                <span className="text-sm font-medium text-gray-700 text-center max-w-24">
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && <div className="flex-1 h-1 mx-2 bg-gray-200" />}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Render contract row
  const renderContractRow = (contract) => (
    <tr key={contract.contractid} className="hover:bg-gray-50 transition duration-150">
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          {getStatusIcon(contract)}
          <div>
            <div className="font-semibold text-gray-900">{contract.contractname}</div>
            <div className="text-sm text-gray-600 mt-1">
              {contract.company?.companyname || contract.companyid}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Contact: {contract.contactDetails.contactPerson}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900 whitespace-nowrap">
          {formatDate(contract.contractStartDate)} to {formatDate(contract.contractEndDate)}
          {contract.isExpired && (
            <div className="text-xs text-red-600 mt-1 font-medium">Expired</div>
          )}
        </div>
      </td>
      <td className="px-6 py-4">
        {contract.download_url ? (
          <div className="flex flex-col space-y-1">
            <div className="text-xs font-medium text-green-600">Download Available</div>
            <div className="text-xs text-gray-500">Ready for download</div>
          </div>
        ) : (
          <div className="text-xs text-gray-500">No download available</div>
        )}
      </td>
      <td className="px-6 py-4">
        {getWorkflowBadge(contract)}
        {contract.sent_pdf_on_email && (
          <div className="text-xs text-green-600 mt-1">✓ Email Sent</div>
        )}
        {contract.pdf_uploaded && <div className="text-xs text-blue-600 mt-1">✓ PDF Uploaded</div>}
        {contract.pdf_signed && <div className="text-xs text-purple-600 mt-1">✓ PDF Signed</div>}
      </td>

      <td className="px-6 py-4">
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewContract(contract)}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition duration-150"
            title="View contract details"
          >
            <FaEye size={16} />
          </button>

          {getActionButtons(contract)}

          {/* Only show edit button if contract is NOT approved or rejected */}
          {!contract.is_approved && !contract.is_rejected && (
            <button
              onClick={() => {
                setSelectedContract(contract);
                setShowEditModal(true);
              }}
              className="p-2 text-gray-600 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition duration-150"
              title="Edit contract"
            >
              <FaEdit size={16} />
            </button>
          )}

          <button
            onClick={() => {
              setSelectedContract(contract);
              setShowDeleteModal(true);
            }}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition duration-150"
            title="Delete contract"
          >
            <FaTrash size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Back to Contracts */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 font-medium mb-6 transition duration-200"
        >
          <FaArrowLeft className="text-lg" />
          <span className="text-lg">Back to Contracts</span>
        </button>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Contract Approvals</h1>
              <p className="text-gray-600 mt-1">Manage contract approval workflow and renewals</p>
            </div>
          </div>

          {/* Enhanced Workflow Steps */}
          <WorkflowSteps />

          {/* Filter Bar */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1 w-full">
              {/* Search Input */}
              <div className="relative flex-1 max-w-md">
                <FaSearch className="absolute left-3 top-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search contract by name or company"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500"
                />
              </div>

              {/* Status Filter */}
              <div className="flex gap-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-900"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>

            {/* Refresh Button */}
            <button
              onClick={fetchContracts}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <FaSync className="text-sm" />
                  <span>Refresh</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Contracts Table */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-200">
                  Contract Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-200">
                  Duration
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-200">
                  Download Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-200">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-b border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading contracts...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-600">
                    <FaFileContract className="mx-auto text-4xl text-gray-300 mb-3" />
                    <p>No contracts found</p>
                  </td>
                </tr>
              ) : (
                filteredContracts
                  .slice(startIndex, startIndex + itemsPerPage)
                  .map(renderContractRow)
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-white border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to{' '}
                {Math.min(startIndex + itemsPerPage, filteredContracts.length)} of{' '}
                {filteredContracts.length} contracts
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-lg font-medium transition duration-200 ${
                      currentPage === page
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Contract Modal */}
      {showViewModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto border border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-100">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-r from-gray-900 to-black rounded-2xl">
                  <FaFileContract className="text-2xl text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">Contract Details</h2>
                  <p className="text-gray-600 mt-1">Complete contract information and status</p>
                </div>
              </div>
              <div className="flex space-x-3">{getWorkflowBadge(selectedContract)}</div>
            </div>

            {/* Main Content Grid */}
            <div className="space-y-8">
              {/* Contract Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Basic Information</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Contract Name</span>
                        <span className="text-sm font-semibold text-gray-900 text-right">
                          {selectedContract.contractname}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Company</span>
                        <span className="text-sm font-semibold text-gray-900 text-right">
                          {selectedContract.company?.companyname || selectedContract.companyid}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Contract ID</span>
                        <span className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
                          {selectedContract.contractid}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">GST Number</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.gst_number || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Contact Information</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Contact Person</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.contactDetails.contactPerson}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Email</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.contactDetails.contactEmail}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">Phone</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.contactDetails.contactPhone || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Duration & Status</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600 block mb-1">
                          Contract Period
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatDate(selectedContract.contractStartDate)} to{' '}
                          {formatDate(selectedContract.contractEndDate)}
                        </span>
                        {selectedContract.isExpired && (
                          <div className="inline-flex items-center space-x-1 mt-2 px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                            <FaTimesCircle className="text-xs" />
                            <span>Contract Expired</span>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Auto Renewal</span>
                        <span
                          className={`text-sm font-semibold ${selectedContract.autoRenewal ? 'text-green-600' : 'text-gray-600'}`}
                        >
                          {selectedContract.autoRenewal ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">Renewal Notice</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.renewalNoticePeriod} days
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Signatory Details */}
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                      <span>Signatory Details</span>
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Signatory Name</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {selectedContract.signatoryDetails.signatoryName}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Title</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.signatoryDetails.signatoryTitle}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-100">
                        <span className="text-sm font-medium text-gray-600">Email</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.signatoryDetails.signatoryEmail}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm font-medium text-gray-600">Phone</span>
                        <span className="text-sm text-gray-900">
                          {selectedContract.signatoryDetails.signatoryPhone || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 shadow-sm">
                <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
                  <span>Financial Details</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedContract.paymentTerms?.currency || 'INR'}{' '}
                      {selectedContract.paymentTerms?.baseprice?.toLocaleString() || '0'}
                    </div>
                    <div className="text-sm font-medium text-gray-600">Base Price</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedContract.discountStructure?.flatDiscount ||
                        selectedContract.discountStructure?.discountSlabs?.[0]?.discountPercent ||
                        0}
                      %
                    </div>
                    <div className="text-sm font-medium text-gray-600 capitalize">
                      {selectedContract.discountStructure?.discountType || 'flat'} Discount
                    </div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedContract.paymentTerms?.currency || 'INR'}{' '}
                      {selectedContract.paymentTerms?.creditlimit?.toLocaleString() || '0'}
                    </div>
                    <div className="text-sm font-medium text-gray-600">Credit Limit</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                    <div className="text-2xl font-bold text-gray-900 mb-2 capitalize">
                      {selectedContract.paymentTerms?.billingCycle || 'monthly'}
                    </div>
                    <div className="text-sm font-medium text-gray-600">Billing Cycle</div>
                  </div>
                </div>
              </div>
              {/* Access Link Status */}
              {selectedContract.accessLink && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200 shadow-sm">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Contract Access Link
                      </h3>
                      <div className="flex items-center space-x-4">
                        <div
                          className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full ${isLinkExpired(selectedContract) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${isLinkExpired(selectedContract) ? 'bg-red-500' : 'bg-green-500'}`}
                          ></div>
                          <span className="text-sm font-medium">
                            {isLinkExpired(selectedContract)
                              ? 'Link Expired'
                              : `Active - Expires in ${getTimeRemaining(selectedContract)}`}
                          </span>
                        </div>
                        {selectedContract.linkGeneratedAt && (
                          <span className="text-sm text-gray-500">
                            Generated: {formatDate(selectedContract.linkGeneratedAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    {!isLinkExpired(selectedContract) && (
                      <button
                        onClick={() => window.open(selectedContract.accessLink, '_blank')}
                        className="px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-all duration-200 font-semibold flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <FaLink size={16} />
                        <span>Open Contract Document</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end space-x-4 pt-8 mt-8 border-t border-gray-100">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-8 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Signed Contract Modal */}
      {showUploadModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaUpload className="text-blue-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-900">Upload Signed Contract</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Upload the signed PDF contract for{' '}
              <span className="font-semibold text-gray-900">"{selectedContract.contractname}"</span>
            </p>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Signed PDF File *
              </label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
              />
              <p className="text-xs text-gray-500 mt-1">Only signed PDF files are accepted</p>
            </div>
            {uploadedFile && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700">
                  Selected file: <span className="font-semibold">{uploadedFile.name}</span>
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium"
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadedFile(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-200 font-medium"
                onClick={() => handleUploadSignedContract(selectedContract.contractid)}
              >
                Upload Signed Contract
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Access Link Modal */}
      {showRegenerateModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaRedo className="text-orange-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-900">Regenerate Access Link</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Generate a new access link for{' '}
              <span className="font-semibold text-gray-900">"{selectedContract.contractname}"</span>
            </p>
            <p className="text-sm text-gray-600 mb-6">
              This will create a new time-limited access link for the contract. The previous link
              will expire.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Expiry Time (optional)
              </label>
              <select
                onChange={(e) => {
                  const minutes = e.target.value ? parseInt(e.target.value) : null;
                  // This would be passed to the API call
                }}
                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
              >
                <option value="">Default expiry (30 minutes)</option>
                <option value="60">1 hour</option>
                <option value="120">2 hours</option>
                <option value="1440">24 hours</option>
                <option value="10080">7 days</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Leave empty for default expiration time</p>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium"
                onClick={() => setShowRegenerateModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition duration-200 font-medium flex items-center space-x-2"
                onClick={() => handleRegenerateAccessLink(selectedContract.contractid)}
                disabled={regeneratingLink}
              >
                <FaRedo size={16} />
                <span>{regeneratingLink ? 'Regenerating...' : 'Regenerate Link'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Contract Modal */}
      {showApproveModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaThumbsUp className="text-green-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-900">Approve Contract</h2>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to approve the contract{' '}
              <span className="font-semibold text-gray-900">"{selectedContract.contractname}"</span>
              ?
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Company:{' '}
              <span className="font-semibold">
                {selectedContract.company?.companyname || selectedContract.companyid}
              </span>
            </p>
            <p className="text-sm text-green-600 font-semibold mb-6">
              This will activate the contract and make it effective immediately.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium"
                onClick={() => setShowApproveModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 font-medium"
                onClick={() => handleApproveContract(selectedContract.contractid)}
              >
                Approve Contract
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Contract Modal */}
      {showRejectModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaThumbsDown className="text-red-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-900">Reject Contract</h2>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to reject the contract{' '}
              <span className="font-semibold text-gray-900">"{selectedContract.contractname}"</span>
              ?
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Company:{' '}
              <span className="font-semibold">
                {selectedContract.company?.companyname || selectedContract.companyid}
              </span>
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows="3"
                className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
                placeholder="Please provide a reason for rejecting this contract..."
                required
              />
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 font-medium"
                onClick={() => handleRejectContract(selectedContract.contractid)}
              >
                Reject Contract
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renew Contract Modal */}
      {showRenewModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaSync className="text-green-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-900">Renew Contract</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Renew expired contract{' '}
              <span className="font-semibold text-gray-900">"{selectedContract.contractname}"</span>
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Start Date *
                </label>
                <input
                  type="date"
                  value={renewalData.newStartDate}
                  onChange={(e) =>
                    setRenewalData((prev) => ({
                      ...prev,
                      newStartDate: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New End Date *
                </label>
                <input
                  type="date"
                  value={renewalData.newEndDate}
                  onChange={(e) =>
                    setRenewalData((prev) => ({
                      ...prev,
                      newEndDate: e.target.value,
                    }))
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
                  min={renewalData.newStartDate || new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Renewal Notes
                </label>
                <textarea
                  value={renewalData.renewalNotes}
                  onChange={(e) =>
                    setRenewalData((prev) => ({
                      ...prev,
                      renewalNotes: e.target.value,
                    }))
                  }
                  rows="2"
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white text-gray-900"
                  placeholder="Any notes about this renewal..."
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium"
                onClick={() => {
                  setShowRenewModal(false);
                  setRenewalData({
                    newStartDate: '',
                    newEndDate: '',
                    renewalNotes: '',
                  });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition duration-200 font-medium"
                onClick={() => handleRenewContract(selectedContract.contractid)}
              >
                Renew Contract
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedContract && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FaTrash className="text-red-600 text-xl" />
              <h2 className="text-xl font-semibold text-gray-900">Delete Contract</h2>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete the contract{' '}
              <span className="font-semibold text-gray-900">"{selectedContract.contractname}"</span>
              ?
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Company:{' '}
              <span className="font-semibold">
                {selectedContract.company?.companyname || selectedContract.companyid}
              </span>
            </p>
            <p className="text-sm text-red-600 font-semibold mb-6">
              This action cannot be undone and will permanently remove the contract data.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-200 font-medium"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-200 font-medium"
                onClick={() => handleDeleteContract(selectedContract.contractid)}
              >
                Delete Contract
              </button>
            </div>
          </div>
        </div>
      )}
      <EditContractModal
        contract={selectedContract}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onUpdate={handleUpdateContract}
      />
    </div>
  );
};

export default ContractApprovalsPage;
